import logging from "utils/logging";
import React, { useRef } from "react";
import { useDispatch } from "react-redux";
import { Fade } from "@mui/material";
import { styled } from "@mui/material/styles";
import Provider from "provider";
import { updateGameStateAction } from "../../gamestate/stateActions";
import * as Constants from "../../gamestate/constants";
import QuestionCountdown from "../../questionCountdown/QuestionCountdown";

import {
  GAMESTATE_ROUND_FINAL,
  GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
  GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
} from "../../gamestate/constants";

const log = logging.getLogger("GameBoardQuestion"); // eslint-disable-line no-unused-vars

// Brand tokens — see public/design/tokens.css
const BRAND_TEAL = '#2ec4b6';
const BRAND_GOLD = '#ffe066';

const ClueHeader = styled('span')({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontWeight: 600,
  fontSize: '1.1rem',
  letterSpacing: '0.12em',
  color: BRAND_TEAL,
});

// Long clues need to shrink; clamp keeps it readable but never pushes the
// answer reveal out of the modal. Tuned against ~32-word clues.
const ClueText = styled('p')({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontWeight: 700,
  fontSize: 'clamp(1.4rem, calc(1rem + 1.6vw), 2.6rem)',
  lineHeight: 1.18,
  letterSpacing: '2px',
  textShadow: '2px 2px 4px rgba(0,0,0,0.35)',
  margin: 0,
  maxWidth: '100%',
});

const CorrectAnswer = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderTop: `2px solid ${BRAND_TEAL}55`,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const CorrectAnswerHeader = styled('p')({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontWeight: 600,
  fontSize: '0.85rem',
  letterSpacing: '0.3em',
  textTransform: 'uppercase',
  color: BRAND_TEAL,
  margin: '0 0 8px',
});

// Korinna is the classic Jeopardy answer typeface — loaded via src/index.css.
const CorrectAnswerText = styled('p')({
  fontFamily: 'Korinna, "Poppins", serif',
  fontWeight: 700,
  fontSize: 'clamp(1.8rem, calc(1.2rem + 2vw), 3rem)',
  lineHeight: 1.1,
  color: BRAND_GOLD,
  textShadow: '0 2px 10px rgba(255,224,102,0.35), 0 2px 8px rgba(0,0,0,0.4)',
  letterSpacing: '0.02em',
  margin: 0,
});

const GameBoardQuestionHeader = ({
  showPoints,
  category,
  pointsLabel,
  isWager,
}) => {
  return (
    <div>
      <ClueHeader>{category.toUpperCase()}</ClueHeader>
      {showPoints && (
        <React.Fragment>
          <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
          <ClueHeader>
            {isWager ? "Wager: " : ""}
            {pointsLabel}
          </ClueHeader>
        </React.Fragment>
      )}
    </div>
  );
};

const GameBoardClue = ({ clueText }) => {
  return <ClueText>{clueText.toUpperCase()}</ClueText>;
};

const GameBoardQuestionCountdown = ({ gameId, gameState, questionObject }) => {
  const dispatch = useDispatch();
  // QuestionCountdown's 100ms poll has no deps array, so onTimeReachedZero
  // can fire many times before this component unmounts. Guard against it.
  const firedRef = useRef(false);

  if (!questionObject) {
    return null;
  }

  if (gameState.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING) {
    return null;
  }

  const onTimeReachedZero = async () => {
    if (firedRef.current) return;
    firedRef.current = true;

    const isFinal = gameState.round === GAMESTATE_ROUND_FINAL;
    const isDD = !!gameState.dailyDouble;

    // For Final/DD, give last-second submissions a grace window to arrive,
    // then mark wager-only submissions incorrect so JudgingComplete can
    // advance. Regular rounds skip this — they have no wager-status subs.
    if (isFinal || isDD) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        await Provider.closeQuestionSubmissions(gameId, gameState.questionId);
      } catch (e) {
        log.error(
          "Failed to close question submissions, advancing anyway:",
          e
        );
      }
    }

    const { endTime, ...rest } = gameState;
    const newState = {
      ...rest,
      roundState: Constants.GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
    };
    dispatch(updateGameStateAction(gameId, newState));
  };

  return (
    <QuestionCountdown
      endTimeStr={questionObject.endTime}
      startTimeStr={questionObject.startTime}
      onTimeReachedZero={onTimeReachedZero}
      roundState={gameState.roundState}
      shouldPlayFinalMusic={gameState.round === GAMESTATE_ROUND_FINAL}
    />
  );
};

const GameBoardQuestionResults = ({ gameState, answer }) => {
  if (gameState.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_RESULTS) {
    return null;
  }

  return (
    <Fade
      timeout={800}
      in={gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_RESULTS}
    >
      <CorrectAnswer>
        <CorrectAnswerHeader>Answer</CorrectAnswerHeader>
        <CorrectAnswerText>{answer}</CorrectAnswerText>
      </CorrectAnswer>
    </Fade>
  );
};

export {
  GameBoardQuestionHeader,
  GameBoardClue,
  GameBoardQuestionCountdown,
  GameBoardQuestionResults,
};
